import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { Tournament, TournamentList } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import {
  createCashOutColumn,
  createEditColumn,
  createIsActiveColumn,
  createSportIconColumn,
} from '../../helpers/columns';
import { defaultColumnsTournaments } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import usePermissions from '../../hooks/usePermissions';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import Switch from '../atoms/Switch';
import TableIconContainer from '../atoms/TableIconContainer';
import TableTemplate from '../templates/TableTemplate';

const TournamentsPage = () => {
  const {
    data: tournamentsData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<TournamentList>('tournaments');
  const { deleteData, updateData } = useMutateData('tournaments', [QUERY_KEYS.tournaments]);
  const { handleSort } = useSort(changeQuery);
  const { hasPermission } = usePermissions();
  const showCashout = hasPermission('manageCashOut');

  const columns: GridColDef[] = [
    createColumn('id', 'ID', {
      sortable: false,
    }),
    createColumn('sportName', 'Sport', {
      valueGetter: (value, row, column, apiRef) => row.sport.name,
    }),
    createSportIconColumn<Tournament>(),
    createColumn('competitionName', 'Competition', {
      valueGetter: (value, row, column, apiRef) => row.competition.name,
    }),
    createColumn('name', 'Tournament Name', {
      minWidth: 150,
    }),
    createColumn('shortName', 'Tournament Short Name'),
    createColumn('position', 'Position'),
    createColumn('livePosition', 'Live Position'),
    createIsActiveColumn<Tournament>({
      updateData,
    }),

    createColumn('topLeague', 'Top League', {
      renderCell: (params) => {
        return (
          <Switch
            value={params.value}
            onChange={(e) => {
              updateData(params.row.id, { topLeague: e.target.checked });
            }}
            name='topLeague'
          />
        );
      },
    }),
    createColumn('topOffer', 'Top Offer', {
      renderCell: (params) => {
        return (
          <Switch
            value={params.value}
            onChange={(e) => {
              updateData(params.row.id, { topOffer: e.target.checked });
            }}
            name='topOffer'
          />
        );
      },
    }),
    createColumn('topOfferPosition', 'Top Offer Position'),
    createColumn('featured', 'Featured Tournament', {
      renderCell: (params) => {
        return (
          <Switch
            value={params.value}
            onChange={() => {
              updateData(params.row.id, { featured: !params.value });
            }}
            name='featured'
          />
        );
      },
    }),
    createColumn('information', 'Information', {
      sortable: false,
      renderCell: (params) =>
        params.row.information && (
          <TableIconContainer>
            <Tooltip title={params.row.information} enterTouchDelay={0} leaveTouchDelay={3000}>
              <InfoIcon color='primary' />
            </Tooltip>
          </TableIconContainer>
        ),
    }),
    createEditColumn<Tournament>({
      deleteData,
      columnName: 'Actions',
    }),
  ];
  showCashout && columns.splice(-1, 0, createCashOutColumn<Tournament>({ updateData, modalEntity: 'tournament' }));

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  return (
    <TableTemplate
      rows={tournamentsData?.items || []}
      columns={columns}
      rowCount={tournamentsData?.count ?? 0}
      loading={isFetching || isLoading}
      defaultVisibleColumns={defaultColumnsTournaments}
      createItemBtnLabel='Add new tournament'
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      formType='tournaments'
      changeQuery={changeQuery}
      showExport
    />
  );
};

export default TournamentsPage;
